import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileWarning, Fingerprint, Printer } from 'lucide-react'
import { useState } from 'react'

import { downloadLoanDisclosure, downloadLoanStatement } from '@/api/documents-api'
import { loanCycleQueryOptions } from '@/api/loans-api'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ViewDocumentButton } from '@/components/document-viewer'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  canCancel,
  canDecide,
  canDisburse as isDisbursable,
  canRecordAppraisal,
  canRecordRecovery,
  cycleStatusLabel,
  isLive,
} from '@/features/loans/constants'
import {
  CycleBalances,
  CycleClassificationPanel,
  CycleFeesPanel,
  CycleGuaranteesPanel,
  CycleOverviewPanel,
  CycleSchedulePanel,
  CycleTermsPanel,
} from '@/features/loans/cycle-panels'
import { LoanLink } from '@/features/loans/loan-link'
import {
  loanPaths,
  useLoanNavigate,
  useLoanParams,
  useRawLoanSearch,
  type CycleOperation,
} from '@/features/loans/loan-paths'
import { useCancelRound, useRejectRound } from '@/features/loans/mutations'
import { useLoanPermissions } from '@/features/loans/permissions'
import { ReasonDialog } from '@/features/loans/reason-dialog'

import { loanCycleDetailSearchSchema, parseLoanSearch, type LoanCycleTab } from './search'

const PANEL_ID = 'loan-cycle-panel'

/**
 * One round: the application, what was decided, what it costs, and what is left.
 *
 * This screen reads on a phone and acts on none of it. Every command that keys an amount, moves
 * money, forgives a debt or is countersigned lives on a screen of its own that refuses below the
 * `lg` breakpoint — see docs/mobile-policy.md. The two exceptions are refusing an application and
 * calling a round off: they key nothing but a reason, move nothing, and are not countersigned, so
 * a manager can do both from wherever they are.
 */
export function LoanCycleDetailPage() {
  const params = useLoanParams()
  const accountId = params.accountId ?? ''
  const cycleId = params.cycleId ?? ''

  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const search = parseLoanSearch(loanCycleDetailSearchSchema, useRawLoanSearch())

  const [dialog, setDialog] = useState<'reject' | 'cancel' | null>(null)
  const [auditOpen, setAuditOpen] = useState(false)

  const query = useQuery(loanCycleQueryOptions(cycleId))
  const reject = useRejectRound(cycleId, accountId)
  const cancel = useCancelRound(cycleId, accountId)

  const tab: LoanCycleTab = search.tab ?? 'round'

  const selectTab = (next: LoanCycleTab) => {
    navigate(loanPaths.cycle(accountId, cycleId), { search: { tab: next }, replace: true })
  }

  const status = query.data?.summary.status

  const operation = (label: string, to: CycleOperation, enabled: boolean) =>
    enabled ? (
      <Button asChild variant="secondary" size="sm" key={to}>
        <LoanLink to={loanPaths.cycleOperation(accountId, cycleId, to)}>{label}</LoanLink>
      </Button>
    ) : null

  return (
    <>
      <LoanLink
        to={loanPaths.account(accountId)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to the account
      </LoanLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-9 w-full max-w-xl" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This round could not be opened"
        />
      ) : status === undefined ? null : (
        <>
          <PageHeader
            title={`Round ${query.data.summary.cycleNumber}`}
            reference={query.data.summary.displayReference}
            description={cycleStatusLabel(status)}
            badges={
              <>
                <StatusBadge status={status} />
                {query.data.summary.isRestructured ? (
                  <StatusBadge status="Restructured" />
                ) : null}
                {query.data.summary.classificationCode === null ? null : (
                  <StatusBadge status={query.data.summary.classificationCode} />
                )}
              </>
            }
            actions={
              <>
                {permissions.canViewStatement ? (
                  <ViewDocumentButton
                    variant="secondary"
                    title={`Loan statement ${query.data.summary.displayReference}`}
                    load={() => downloadLoanStatement(cycleId)}
                  >
                    <Printer />
                    Print statement
                  </ViewDocumentButton>
                ) : null}
                {permissions.canGenerateDisclosure &&
                (status === 'Approved' || status === 'PendingDisbursement') ? (
                  <ViewDocumentButton
                    variant="secondary"
                    title={`Loan disclosure ${query.data.summary.displayReference} (draft)`}
                    description="Marked DRAFT on the page: the rates are previews over an assumed disbursement of today, and are recomputed for real when the round is released."
                    load={() => downloadLoanDisclosure(cycleId)}
                  >
                    <FileWarning />
                    Print disclosure (draft)
                  </ViewDocumentButton>
                ) : null}
                <Button variant="secondary" onClick={() => setAuditOpen(true)}>
                  <Fingerprint />
                  Audit trail
                </Button>
              </>
            }
          />

          <CycleBalances cycle={query.data.summary} />

          <div className="flex flex-wrap items-center gap-2">
            {operation(
              'Record appraisal',
              'appraise',
              permissions.canAppraise && canRecordAppraisal(status),
            )}
            {operation('Approve', 'approve', permissions.canApprove && canDecide(status))}
            {permissions.canApprove && canDecide(status) ? (
              <Button variant="secondary" size="sm" onClick={() => setDialog('reject')}>
                Refuse
              </Button>
            ) : null}
            {permissions.canApply && canCancel(status) ? (
              <Button variant="ghost" size="sm" onClick={() => setDialog('cancel')}>
                Call the round off
              </Button>
            ) : null}
            {operation('Disburse', 'disburse', permissions.canDisburse && isDisbursable(status))}
            {operation(
              'Post a repayment',
              'repay',
              permissions.canPostRepayment && isLive(status),
            )}
            {operation('Settle early', 'settle', permissions.canPostRepayment && isLive(status))}
            {operation(
              'Waive penalty',
              'waive',
              permissions.canWaivePenalty && isLive(status),
            )}
            {operation('Restructure', 'restructure', permissions.canRestructure && isLive(status))}
            {operation('Write off', 'write-off', permissions.canWriteOff && isLive(status))}
            {operation(
              'Execute the guarantee',
              'offset',
              permissions.canOffsetGuarantee && isLive(status),
            )}
            {operation(
              'Record a recovery',
              'recovery',
              permissions.canPostRepayment && canRecordRecovery(status),
            )}
            {operation(
              'Override the class',
              'classify',
              permissions.canOverrideClassification && isLive(status),
            )}
          </div>

          {query.data.summary.daysInArrears > 0 ? (
            <Alert tone="danger">
              <AlertTitle>
                {query.data.summary.daysInArrears} days in arrears
              </AlertTitle>
              <AlertDescription>
                The ageing run is what moves a round into arrears and accrues penalty against it.
                Collections, the group guarantee and a restructure are the three ways out of this
                before a write-off.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-4">
            <TabStrip<LoanCycleTab>
              tabs={
                [
                  { id: 'round', label: 'Round' },
                  { id: 'schedule', label: 'Schedule' },
                  { id: 'terms', label: 'Terms' },
                  { id: 'fees', label: 'Fees', badge: query.data.fees.length },
                  { id: 'guarantees', label: 'Guarantees', badge: query.data.guarantees.length },
                  { id: 'classification', label: 'Classification' },
                ] satisfies TabDefinition<LoanCycleTab>[]
              }
              active={tab}
              onChange={selectTab}
              panelId={PANEL_ID}
              label="Round sections"
            />

            <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
              {tab === 'round' ? <CycleOverviewPanel cycle={query.data} /> : null}
              {tab === 'schedule' ? <CycleSchedulePanel cycle={query.data} /> : null}
              {tab === 'terms' ? <CycleTermsPanel cycle={query.data} /> : null}
              {tab === 'fees' ? <CycleFeesPanel cycle={query.data} /> : null}
              {tab === 'guarantees' ? <CycleGuaranteesPanel cycle={query.data} /> : null}
              {tab === 'classification' ? <CycleClassificationPanel cycle={query.data} /> : null}
            </div>
          </div>

          <ReasonDialog
            open={dialog === 'reject'}
            onOpenChange={(open) => setDialog(open ? 'reject' : null)}
            title="Refuse this application"
            description="The round is closed as refused. It cannot be reopened — a further application is a new round."
            label="Reason"
            placeholder="Why the application was turned down."
            hint="This is what the applicant will be told, so write it for them."
            confirmLabel="Refuse the application"
            tone="danger"
            isPending={reject.isPending}
            error={reject.isError ? reject.error : null}
            onConfirm={(reason) => {
              reject.mutate({ reason }, { onSuccess: () => setDialog(null) })
            }}
          />

          <ReasonDialog
            open={dialog === 'cancel'}
            onOpenChange={(open) => setDialog(open ? 'cancel' : null)}
            title="Call the round off"
            description="Before any money has moved. Withdrawn if nobody has decided it yet, cancelled if somebody has — the server records which."
            label="Reason"
            placeholder="Why the round is not going ahead."
            hint="A disbursement still waiting for a countersignature can be called off: it has moved no money."
            confirmLabel="Call it off"
            tone="danger"
            isPending={cancel.isPending}
            error={cancel.isError ? cancel.error : null}
            onConfirm={(reason) => {
              cancel.mutate({ reason }, { onSuccess: () => setDialog(null) })
            }}
          />

          <AuditTrailDrawer
            open={auditOpen}
            onOpenChange={setAuditOpen}
            entityType="LoanCycle"
            entityId={query.data.summary.id}
            subject={`Round ${query.data.summary.cycleNumber} · ${query.data.summary.displayReference}`}
          />
        </>
      )}
    </>
  )
}
